/**
 * Local image utilities for locally-served optimized images.
 *
 * Images are pre-downloaded to public/images/source/ and optimized to
 * public/images/optimized/ as AVIF + WebP at multiple srcset breakpoints.
 */

export interface ImageMeta { width: number; height: number; }

/** Maps every seqId to its native download dimensions (for CLS prevention). */
export const IMAGE_MANIFEST: Record<string, ImageMeta> = {
  hero_opt_01:                { width: 900,  height: 630 },
  platform_cubeos01a:         { width: 900,  height: 480 },
  platform_dash_opt:          { width: 900,  height: 480 },
  platform_sdav01a:           { width: 700,  height: 900 },
  sdav_what_opt:              { width: 700,  height: 900 },
  cube_mc_og_opt:             { width: 900,  height: 480 },
  cube_mc_og_unified_v4:      { width: 900,  height: 480 },
  cube_mc_og_v3a:             { width: 900,  height: 480 },
  cubegroup_banner_01a:       { width: 900,  height: 200 },
  cs_hotel_hero_2026a:        { width: 1440, height: 600 },
  cs_hotel_og_2026a:          { width: 900,  height: 480 },
  cs_hotel_rel1a:             { width: 900,  height: 570 },
  cube_hotel_opt:             { width: 900,  height: 570 },
  cube_idx_hotel_2026a:       { width: 900,  height: 570 },
  cube_health_opt:            { width: 900,  height: 570 },
  cube_idx_health_2026a:      { width: 900,  height: 570 },
  cs_hc_hero_2026a:           { width: 1440, height: 600 },
  cs_hc_rel1a:                { width: 900,  height: 570 },
  cs_hc_og_2026a:             { width: 900,  height: 480 },
  cs_corp_hero_2026a:         { width: 1440, height: 600 },
  cs_corp_og_2026a:           { width: 900,  height: 480 },
  cs_corp_rel1a:              { width: 900,  height: 570 },
  cube_corp_opt:              { width: 900,  height: 570 },
  cube_idx_corp_2026a:        { width: 900,  height: 570 },
  cs_choices_hero_2026a:      { width: 1440, height: 600 },
  cs_choices_og_2026a:        { width: 900,  height: 480 },
  cs_choices_rel1a:           { width: 900,  height: 570 },
  cube_grocery_opt:           { width: 900,  height: 570 },
  cube_idx_grocery_2026a:     { width: 900,  height: 570 },
  company_bg_img_01a:         { width: 700,  height: 460 },
  calgary_office_01a:         { width: 480,  height: 300 },
  charlottesville_office_01a: { width: 480,  height: 300 },
};

const BREAKPOINTS = [400, 800, 1200, 1920];

/** Build a responsive srcset string pointing at locally-served optimized files. */
export function buildLocalSrcset(seqId: string, format: "avif" | "webp"): string {
  const meta = IMAGE_MANIFEST[seqId];
  if (!meta) return `/images/source/${seqId}.jpg`;
  const bps = BREAKPOINTS.filter((bp) => bp <= meta.width);
  if (!bps.length) return `/images/source/${seqId}.jpg ${meta.width}w`;
  return bps.map((bp) => `/images/optimized/${seqId}-${bp}w.${format} ${bp}w`).join(", ");
}

/** JPEG source fallback path (also used for OG images with an absolute base URL). */
export function localFallbackPath(seqId: string): string {
  return `/images/source/${seqId}.jpg`;
}

/** Native dimensions from the manifest, or null if unknown. */
export function nativeDimensions(seqId: string): ImageMeta | null {
  return IMAGE_MANIFEST[seqId] ?? null;
}

/**
 * Build a sizes attribute string for common layout patterns.
 *
 * Sizes hints tell the browser which srcset entry to pick based on the
 * rendered image size (not the viewport). Pick the pattern closest to
 * your actual layout.
 */
export const SIZES = {
  /** Full-bleed hero / banner image */
  fullBleed: "100vw",

  /** Half-width image in a two-column grid (desktop), full-width (mobile) */
  halfGrid: "(max-width: 1023px) 100vw, 50vw",

  /** One-third column layout */
  oneThird: "(max-width: 1023px) 100vw, 33vw",

  /** Card image in a 2-col grid */
  card2Col: "(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 600px",

  /** Card image in a 3-col grid */
  card3Col: "(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 33vw",

  /** Thumbnail / small sidebar image */
  thumbnail: "(max-width: 767px) 100px, 200px",

  /** Portrait image in a side-by-side layout */
  portraitSide: "(max-width: 1023px) 100vw, 45vw",

  /** Content image inside article body (max ~700px) */
  articleBody: "(max-width: 767px) 100vw, 700px",
} as const;

/**
 * Default loading strategy per position:
 * - Above-the-fold (top 1-2 screens): eager + high fetchPriority
 * - Below-the-fold: lazy
 */
export function imageLoadingStrategy(
  isAboveFold: boolean
): {
  loading: "eager" | "lazy";
  fetchPriority: "high" | "low" | "auto";
  decoding: "sync" | "async";
} {
  if (isAboveFold) {
    return {
      loading: "eager",
      fetchPriority: "high",
      decoding: "sync",
    };
  }
  return {
    loading: "lazy",
    fetchPriority: "auto",
    decoding: "async",
  };
}
